/* Music player: one hidden <audio> plays the playlist, the fixed card shows play/pause,
 * the track title and "Artist · Playlist". The track, position and play/pause choice are
 * saved in sessionStorage, so each page carries on where the last one left off. */
(() => {
  if (customElements.get('music-playlist')) return;

  const STATE_KEY = 'music-playlist';

  class MusicPlaylist extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('[data-player-media]');
      this.tracks = [...this.querySelectorAll('[data-track]')];
      this.card = this.querySelector('[data-player-card]');
      this.toggleButton = this.querySelector('[data-action="toggle"]');
      this.progress = this.querySelector('[data-progress]');
      this.nowTitle = this.querySelector('[data-now-title]');
      this.nowSub = this.querySelector('[data-now-sub]');
      this.playlistName = this.dataset.playlistName;
      this.autoplayNext = this.dataset.autoplayNext === 'true';
      this.loop = this.dataset.loop === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Measure the sub line again when the card's width or font changes
      window.matchMedia('(max-width: 749px)').addEventListener('change', () => this.setSub(this.subText));
      document.fonts.ready.then(() => this.setSub(this.subText));
      this.index = -1;

      this.toggleButton.addEventListener('click', () => this.toggle());

      this.media.addEventListener('play', () => {
        this.wantsPlay = true;
        this.onPlayState(true);
        this.saveState();
      });
      this.media.addEventListener('pause', () => {
        this.onPlayState(false);
        // Only save the visitor's own pause, not the automatic ones below or a track change
        if (this.hiddenPause || this.leaving || !this.media.paused) return;
        this.wantsPlay = false;
        this.saveState();
      });
      this.media.addEventListener('loadedmetadata', () => {
        if (this.pendingTime) this.media.currentTime = this.pendingTime;
        this.pendingTime = 0;
      });
      this.media.addEventListener('timeupdate', () => {
        this.onTime();
        this.saveState();
      });
      this.media.addEventListener('ended', () => this.onEnded());

      // Phones and tablets: pause when the visitor switches tab or app, locks the screen or closes the
      // browser. Coming back to the paused player counts as their pause, so the next pages stay paused.
      const touchDevice = window.matchMedia('(pointer: coarse)').matches;
      document.addEventListener('visibilitychange', () => {
        if (!touchDevice) return;
        if (document.hidden) {
          if (!this.media.paused) {
            this.hiddenPause = true;
            this.media.pause();
          }
        } else if (this.hiddenPause) {
          this.hiddenPause = false;
          this.wantsPlay = false;
          this.saveState();
        }
      });
      window.addEventListener('pagehide', () => {
        this.hiddenPause = false;
        this.leaving = true;
        this.saveState();
        this.media.pause();
      });
      // Back/forward button can restore this page as it was left, so load the latest saved state
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.leaving = false;
        this.resume();
      });

      // Hide the card while the footer's country selector bar is in view (not on load, so it still
      // shows on pages too short to scroll)
      const dock = this.querySelector('.music-media__dock');
      const footerBottom = document.querySelector('.supporting-menu');
      if (footerBottom) {
        new IntersectionObserver(([entry]) => dock.classList.toggle('music-media__dock--hidden', entry.isIntersecting && window.scrollY > 0)).observe(footerBottom);
      }

      this.resume();
    }

    // Load the saved track, position and play/pause choice. Until the visitor has played or paused,
    // the "Start playing automatically" setting decides.
    resume() {
      const state = this.readState();
      const saved = this.tracks[state?.index] ? state : null;
      this.select(saved?.index ?? 0, false);
      if (saved?.time) {
        if (this.media.readyState) this.media.currentTime = saved.time;
        else this.pendingTime = saved.time;
      }
      // The audio only downloads once it plays, so show the saved progress until then
      if (this.progress && saved?.progress) this.progress.style.width = saved.progress;
      this.wantsPlay = saved?.playing ?? null;
      if (window.Shopify?.designMode) return;
      if (this.wantsPlay ?? this.dataset.autoplay === 'true') this.autoplay();
    }

    readState() {
      try { return JSON.parse(sessionStorage.getItem(STATE_KEY)); } catch (error) { return null; }
    }

    saveState() {
      const state = {
        index: this.index,
        time: this.pendingTime || this.media.currentTime,
        progress: this.progress?.style.width,
        playing: this.wantsPlay,
      };
      try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (error) {}
    }

    select(index, play) {
      const item = this.tracks[index];
      if (index !== this.index) {
        this.index = index;
        this.pendingTime = 0;
        this.media.pause();
        this.media.src = item.dataset.src;
        this.media.load();
        this.nowTitle.textContent = item.dataset.title;
        this.setSub([item.dataset.artist, this.playlistName].filter(Boolean).join(' · '));
        if (this.progress) this.progress.style.width = '0%';
      }
      if (play) this.play();
    }

    // When "Artist · Playlist" doesn't fit the card, scroll two copies of it in a loop (speed is set in the CSS)
    setSub(text = '') {
      this.subText = text;
      this.nowSub.textContent = text;
      this.nowSub.classList.remove('is-marquee');
      if (this.reducedMotion.matches || this.nowSub.scrollWidth <= this.nowSub.clientWidth) return;

      const track = document.createElement('span');
      track.className = 'music-player__sub-track';
      [false, true].forEach((copy) => {
        const span = document.createElement('span');
        span.textContent = text;
        if (copy) span.setAttribute('aria-hidden', 'true');
        track.append(span);
      });
      this.nowSub.replaceChildren(track);
      this.nowSub.classList.add('is-marquee');
    }

    play() {
      this.media.play().catch(() => this.onPlayState(false));
    }

    // Autoplay or resume. Browsers block sound until the visitor interacts with the page, so if that
    // fails, start on their first click, tap or key press. Skips the play button (it has its own handler)
    // and links, which would only play for a moment before the next page loads.
    autoplay() {
      const events = ['click', 'touchend', 'keydown'];
      const start = (event) => {
        if (event.target.closest('a[href]') || this.toggleButton.contains(event.target)) return;
        this.play();
      };
      events.forEach((type) => document.addEventListener(type, start, true));
      this.media.addEventListener('play', () => events.forEach((type) => document.removeEventListener(type, start, true)), { once: true });
      this.play();
    }

    toggle() { this.media.paused ? this.play() : this.media.pause(); }

    // Track finished: play the next one if set to, starting over after the last when looping
    onEnded() {
      const last = this.index === this.tracks.length - 1;
      if (last && !this.loop) return;
      if (this.autoplayNext) this.select(last ? 0 : this.index + 1, true);
    }

    onPlayState(playing) {
      this.card.classList.toggle('is-playing', playing);
      this.toggleButton.setAttribute('aria-label', playing ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay);
    }

    onTime() {
      if (!this.progress) return;
      const duration = this.media.duration || 0;
      this.progress.style.width = duration ? `${(this.media.currentTime / duration) * 100}%` : '0%';
    }
  }

  customElements.define('music-playlist', MusicPlaylist);
})();
